import { type JSX, type MouseEvent, memo } from "react";
import styled from "styled-components";
import { useMutation } from "urql";
import { currentColors } from "../../shared/colors";
import RenderContent from "../../shared/components/RenderContent";
import { formatYMD, parseDate } from "../../shared/dateFns";
import t from "../../shared/translations";
import { useRefetchUnreadMessages } from "../components/UnreadMessages";
import PrevThread from "./PrevThread";
import { deleteMutation, type IMessage, toggleArchiveMutataion, toggleReadMutation } from "./query";

const MessageWrapper = styled.div`
    border: 1px solid ${currentColors.text};
    margin: 0 0 1px 0;
    border-radius: 0.5rem;
    padding: 0.5rem;
    container-type: inline-size;
`;

interface IMessageHead {
    $unread: boolean;
}

const MessageHead = styled.div<IMessageHead>`
    cursor: pointer;
    font-weight: ${({ $unread }) => ($unread ? "bold" : "normal")};
    display: grid;
    gap: 8px;
    padding: 4px;
    grid-template-columns: 24px 100px 1fr;
    grid-template-areas:
        "checkbox date sender"
        "subject subject subject";

    @container (min-width: 800px) {
        grid-template-columns: 24px 100px minmax(150px, auto) 1fr;
        grid-template-areas:
            "checkbox date sender subject";
    }
`;

const CheckboxCol = styled.label`
    grid-area: checkbox;
    display: flex;
    align-items: center;

    input {
        margin: 0;
        transform: scale(1.2);
    }
`;

const HeaderDate = styled.div`
    grid-area: date;
`;

const HeaderSender = styled.div`
    grid-area: sender;
`;

const HeaderSubject = styled.div`
    grid-area: subject;
`;

const Body = styled.div`
    margin: 0.5rem 0 0 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
`;

const Actions = styled.div`
    margin: 0.5rem 0 0 0;
`;

const FakeLink = styled.span`
    color: ${currentColors.link};
    cursor: pointer;

    &:hover {
        color: ${currentColors.link};
    }
`;

interface IProps {
    message: IMessage;
    open: boolean;
    toggleOpen: (id: string) => void;
    openReply: (message: IMessage) => void;
    selected: boolean;
    onToggleSelected: (id: string) => void;
}

function Message({ message, open, toggleOpen, openReply, selected, onToggleSelected }: IProps): JSX.Element {
    const [readResult, executeRead] = useMutation(toggleReadMutation);
    const [archiveResult, executeArchive] = useMutation(toggleArchiveMutataion);
    const [deleteResult, executeDelete] = useMutation(deleteMutation);

    const refetchUnread = useRefetchUnreadMessages();

    const clickHeader = async () => {
        toggleOpen(message.id);
        if (!open && !message.read && !readResult.fetching) {
            await executeRead({ ids: [message.id], read: true });
            refetchUnread?.();
        }
    };

    const clickArchive = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (!archiveResult.fetching) {
            void executeArchive({ ids: [message.id], archived: true });
        }
    };

    const clickMarkUnread = async (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (!archiveResult.fetching) {
            await executeRead({ ids: [message.id], read: false });
            refetchUnread?.();
        }
    };

    const clickReply = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        openReply(message);
    };

    const clickDelete = async (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (!deleteResult.fetching) {
            if (!window.confirm(t("player.messages.confirm-delete-single"))) {
                return;
            }
            await executeDelete({ ids: [message.id] });
            refetchUnread?.();
        }
    };

    const handleCheckboxClick = (event: MouseEvent<HTMLLabelElement>) => {
        event.stopPropagation();
    };

    const actionLinks = [];
    if (!message.archived) {
        actionLinks.push(
            <FakeLink key="archive" onClick={clickArchive}>
                {t("player.messages.button-archive-message")}
            </FakeLink>,
        );
        actionLinks.push(" | ");
        actionLinks.push(
            <FakeLink key="unread" onClick={clickMarkUnread}>
                {t("player.messages.button-mark-unread-message")}
            </FakeLink>,
        );
    }
    if (actionLinks.length > 0) {
        actionLinks.push(" | ");
    }
    actionLinks.push(
        <FakeLink key="delete" onClick={clickDelete}>
            {t("player.messages.button-delete-message")}
        </FakeLink>,
    );

    if (message.canReply) {
        if (actionLinks.length > 0) {
            actionLinks.push(" | ");
        }
        actionLinks.push(
            <FakeLink key="reply" onClick={clickReply}>
                {t("player.messages.button-reply")}
            </FakeLink>,
        );
    }

    return (
        <MessageWrapper>
            <MessageHead $unread={!message.read} onClick={clickHeader}>
                <CheckboxCol onClick={handleCheckboxClick}>
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                            onToggleSelected(message.id);
                        }}
                    />
                </CheckboxCol>
                <HeaderDate>{formatYMD(parseDate(message.datetime))}</HeaderDate>
                <HeaderSender>{message.senderName}</HeaderSender>
                <HeaderSubject>{message.subject}</HeaderSubject>
            </MessageHead>
            {open && (
                <Body>
                    <PrevThread id={message.id} />
                    <div>
                        <RenderContent content={message.content} contentFormat={message.contentFormat} />
                    </div>
                    <Actions>{actionLinks}</Actions>
                </Body>
            )}
        </MessageWrapper>
    );
}

export default memo(Message);
